import { createFileRoute } from "@tanstack/react-router";
import { type FC, useState } from "react";
import { AI_TUTOR_ID } from "@/constants/settings.ts";
import { useSignalROn } from "@/helpers/signalr.ts";
import { SigROn } from "@/types/signalr-functions.ts";
import type { FlowGeneratorSession, FlowItem } from "@/types/flow.ts";
import { useChatStore } from "@/stores/useChatStore.ts";
import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { IsAiTutorUserId } from "@/helpers/aiTutor.ts";
import { MessageBody } from "@/components/chat/Message.ts";
import { useActiveSessionStore } from "@/stores/useActiveSessionStore.ts";

export const Route = createFileRoute("/ai-stream")({
  component: RouteComponent,
});

const AiStreamContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  flex: 1;

  height: 95.5vh;

  background-color: var(--brand-secondary);
  padding: 1rem ${t.chatPaddingSides};
  gap: 0.75rem;

  border-bottom: ${t.brandBorderSegment};
  overflow-y: scroll;
  overflow-x: clip;
  scrollbar-width: none;

  overflow-anchor: none;
  overscroll-behavior: none;

  @container style(--small-height: 1) {
    gap: 0.2rem;
    padding: 0.5rem ${t.chatPaddingSides};
  }
`;

interface AiSessionMessagesProps {
  session: Partial<FlowGeneratorSession>;
  onMessage: (flow: FlowItem, session: Partial<FlowGeneratorSession>) => void;
}

const AiSessionMessages: FC<AiSessionMessagesProps> = ({ session, onMessage }) => {
  const handleMessage = (flow: FlowItem) => {
    const isStudentMessage = flow.userId === session.userId;
    const isAiMessage = IsAiTutorUserId(flow.userId);

    if (!isStudentMessage && !isAiMessage) return;

    onMessage(flow, session);
  };

  useSignalROn(SigROn.DispatchFlowItem + session.flowGeneratorSessionId, handleMessage);

  return null;
};

enum MessageType {
  Student,
  Tutor,
  Ai,
}

interface Message {
  type: MessageType;
  flowItem: FlowItem;
}

function RouteComponent() {
  const [messageStream, setMessageStream] = useState<Message[]>([]);
  const { AddNewChat, SetChatActive, chats } = useChatStore();
  const { activeSessions } = useActiveSessionStore();

  const AiSessions = activeSessions.get(AI_TUTOR_ID);

  const handleStreamMessage = (flow: FlowItem, session: Partial<FlowGeneratorSession>) => {
    const msg = {
      type: IsAiTutorUserId(flow.userId)
        ? MessageType.Ai
        : flow.userId === session.userId
          ? MessageType.Student
          : MessageType.Tutor,
      flowItem: flow,
    } as Message;
    const newMessages = [msg, ...messageStream];

    if (newMessages.length > 100) newMessages.pop();

    setMessageStream(newMessages);
  };

  const openChat = (flowGeneratorSessionId: number) => {
    if (flowGeneratorSessionId && !chats.get(flowGeneratorSessionId)) {
      AddNewChat({
        flowGeneratorSessionId,
      });
    }

    SetChatActive(flowGeneratorSessionId);
  };

  return (
    <AiStreamContainer>
      {AiSessions?.map((session) => (
        <AiSessionMessages
          key={session.flowGeneratorSessionId}
          session={session}
          onMessage={handleStreamMessage}
        />
      ))}
      {messageStream.length === 0 && <div>Messages will get streamed in over time.</div>}
      {messageStream.map((message) => (
        <MessageBody
          key={message.flowItem.flowItemId}
          data-right={message.type === MessageType.Ai || message.type === MessageType.Tutor}
          data-ai={message.type === MessageType.Ai}
          onClick={() => openChat(message.flowItem.flowGeneratorSessionId)}
          style={{ cursor: "pointer" }}
        >
          {message.flowItem.text}
        </MessageBody>
      ))}
    </AiStreamContainer>
  );
}
